import Hotkeys from 'react-hot-keys';
import { Fragment, PureComponent, ReactNode } from 'react';
import { SlideFromRightWrapper } from '../../../../common/SlideFromRightWrapper';
import AutoSuggest, { TREATMENT_SERVICES_AND_TREATMENT_PACKAGES_AS_INPUT } from './AutoSuggest';
import cloneDeep from 'lodash/cloneDeep';
import createTreatmentPlan from '../../../../../api/createTreatmentPlan';
import { Service, TREATMENT_PLAN_TYPE, TreatmentPlan } from '../../../../../api/getTreatmentPlans';
import { getDoc } from '../../../../../store/idbHandler';
import { v4 } from 'uuid';
import EstimateServicesTable from './EstimateServicesTable';
import { notification } from 'antd';
import { GTreatmentPackage, GTreatmentService } from '../../../../../store/db';
import { captureError, captureErrorString } from '../../../../../pages/captureError';
import { track } from '../../../../../tracking';
import { formattedNumber } from '../../../../../pages/utils';

interface Props {
    closeMe: () => void;
    editEstimate?: TreatmentPlan;
    fetchEstimates: (patientId: string) => void;
    ptDetails: string;
    patientId: string;
    clinicId: string;
}

interface State {
    estimate: TreatmentPlan;
    isSavingEstimate?: boolean;
}

const newEstimate = () => ({
    _id: '',
    docid: getDoc()?._id || '',
    patientid: '',
    created_at: '',
    updated_at: '',
    name: '',
    services: [],
    plan_id: v4(),
    type: TREATMENT_PLAN_TYPE.ESTIMATE,
    discount: 0,
});

type Option = GTreatmentService & { type?: string; services?: GTreatmentService[] };

class CreateNewEstimates extends PureComponent<Props, State> {
    state: State = {
        estimate: this.props.editEstimate || newEstimate(),
    };

    componentDidMount(): void {
        const { editEstimate } = this.props;
        setTimeout(() => {
            const treatmentServicesAndTreatmentPackagesAsInput = document.getElementById(
                TREATMENT_SERVICES_AND_TREATMENT_PACKAGES_AS_INPUT,
            );
            !editEstimate &&
                treatmentServicesAndTreatmentPackagesAsInput &&
                treatmentServicesAndTreatmentPackagesAsInput.focus();
        }, 500);
    }

    onFinish = async () => {
        const { estimate } = this.state;
        if (!estimate?.name.trim()) {
            notification.error({
                message: 'Please enter a treatment package name',
                duration: 5,
            });
            return;
        }
        if (!estimate?.services.length) {
            notification.error({
                message: 'Please add a treatment service',
                duration: 5,
            });
            return;
        }
        this.setState({
            isSavingEstimate: true,
        });
        const { closeMe, fetchEstimates, patientId } = this.props;
        estimate.patientid = patientId;
        if (!estimate.created_at) {
            estimate.created_at = new Date().toISOString();
        }
        estimate.updated_at = new Date().toISOString();
        estimate.docid = getDoc()?._id || '';
        try {
            const { success, error = '' } = await createTreatmentPlan(estimate);
            if (success) {
                fetchEstimates(patientId);
                closeMe();
            } else {
                captureErrorString(error);
                track('ERROR', {
                    on: 'createTreatmentPlan',
                    error: error,
                });
                notification.error({
                    message: 'Error in saving estimate',
                    duration: 5,
                });
            }
        } catch (err) {
            captureError(err as Error);
            notification.error({
                message: 'Error while saving estimate',
                duration: 5,
            });
        } finally {
            this.setState({ isSavingEstimate: false });
        }
        track('CREATE_NEW_ESTIMATES_FINISH', { planId: estimate.plan_id });
    };

    onSelect = (option: Option) => {
        const { estimate } = this.state;
        const { services } = estimate;
        const optionType = option.type;
        if (optionType === 'service') {
            const isServicePresent = services.find((s) => s.id === option.service_id);
            if (!isServicePresent) {
                estimate.services.push({
                    id: option.service_id,
                    name: option.name,
                    teeth_ids: [],
                    surfaces: [],
                    qty: 1,
                    mrp: option.price,
                    discount: 0,
                    amount: option.price,
                    remarks: '',
                });
            }
        } else {
            option.services?.forEach((service) => {
                const isServicePresent = services.find((s) => s.id === service.service_id);
                if (!isServicePresent) {
                    estimate.services.push({
                        id: service.service_id,
                        name: service.name,
                        teeth_ids: [],
                        surfaces: [],
                        qty: 1,
                        mrp: service.price,
                        discount: 0,
                        amount: service.price,
                        remarks: '',
                    });
                }
            });
        }
        this.setState({ estimate: cloneDeep(estimate) });
        track('SERVICES_AND_PACKAGES_AS_SELECT', { optionType });
    };

    updateService = (newService: Service, index: number) => {
        const { estimate } = this.state;
        estimate.services[index] = newService;
        this.setState({
            estimate: cloneDeep(estimate),
        });
        track('UPDATE_SERVICE_IN_ESTIMATE');
    };

    deleteService = (index: number) => {
        const { estimate } = this.state;
        estimate.services.splice(index, 1);
        this.setState({
            estimate: cloneDeep(estimate),
        });
        track('DELETE_SERVICE_IN_ESTIMATE');
    };

    render(): ReactNode {
        const { closeMe, editEstimate, ptDetails, clinicId } = this.props;
        const { estimate, isSavingEstimate } = this.state;
        const { onFinish, onSelect, updateService, deleteService } = this;
        const totalMrp = estimate.services.reduce((acc, cur) => acc + cur.mrp, 0);
        const totalAmount = estimate.services.reduce((acc, cur) => acc + cur.amount, 0);
        const lineItemDiscount = totalMrp - totalAmount;
        const additionalDiscount = (estimate.discount / 100) * totalAmount;
        const amountToBePaid = totalAmount - additionalDiscount;

        return (
            <Hotkeys keyName="esc">
                <SlideFromRightWrapper
                    closeMe={closeMe}
                    style={{ width: '90%', maxWidth: '1200px' }}
                >
                    <div className="h-full flex flex-col overflow-hidden">
                        <div className="bg-white px-16 py-12 flex items-center justify-between border-b border-darwin-neutral_new-200">
                            <div className="flex items-center space-x-8">
                                <p className="text-20 text-darwin-neutrals-1000">
                                    {editEstimate ? 'Edit' : 'New'} Plan Estimate
                                </p>
                                {ptDetails ? (
                                    <div className="flex items-center space-x-4 text-darwin-neutral_new-800 relative top-1">
                                        <p>for</p>
                                        <p className="font-700">{ptDetails}</p>
                                    </div>
                                ) : (
                                    ''
                                )}
                            </div>
                            <svg
                                className="w-14 h-14 fill-darwin-neutral_new-800 cursor-pointer"
                                viewBox="0 0 12 12"
                                xmlns="http://www.w3.org/2000/svg"
                                onClick={closeMe}
                            >
                                <path d="M10.7812 2.28125L7.03125 6.03125L10.75 9.75C11.0625 10.0312 11.0625 10.5 10.75 10.7812C10.4688 11.0938 10 11.0938 9.71875 10.7812L5.96875 7.0625L2.25 10.7812C1.96875 11.0938 1.5 11.0938 1.21875 10.7812C0.90625 10.5 0.90625 10.0312 1.21875 9.71875L4.9375 6L1.21875 2.28125C0.90625 2 0.90625 1.53125 1.21875 1.21875C1.5 0.9375 1.96875 0.9375 2.28125 1.21875L6 4.96875L9.71875 1.25C10 0.9375 10.4688 0.9375 10.7812 1.25C11.0625 1.53125 11.0625 2 10.7812 2.28125Z" />
                            </svg>
                        </div>
                        <div className="flex-1 flex flex-col overflow-hidden bg-darwin-neutrals-50">
                            <div className="flex-1 flex flex-col overflow-auto space-y-16 p-16">
                                <p className="text-darwin-neutrals-1000 font-700 text-16">
                                    Plan details
                                </p>
                                <input
                                    className="px-12 py-10 border border-darwin-neutral_new-200 rounded-8 bg-white"
                                    placeholder="Enter plan name"
                                    value={estimate.name}
                                    onChange={(e) => {
                                        this.setState({
                                            estimate: {
                                                ...estimate,
                                                name: e.target.value,
                                            },
                                        });
                                    }}
                                />
                                <AutoSuggest
                                    onSelect={onSelect}
                                    services={estimate.services}
                                    clinicId={clinicId}
                                />
                                {estimate.services.length > 0 && (
                                    <EstimateServicesTable
                                        services={estimate.services}
                                        updateService={updateService}
                                        deleteService={deleteService}
                                    />
                                )}
                                {estimate.services.length > 0 && (
                                    <div className="flex items-start justify-between border border-darwin-neutral_new-200 px-16 py-12 rounded-8 bg-white">
                                        <div className="space-y-8">
                                            <p className="text-11 font-700">
                                                ADDITIONAL DISCOUNT (%)
                                            </p>
                                            <input
                                                className="text-13 px-12 py-10 border border-darwin-neutral_new-200 rounded-8"
                                                onChange={(e) => {
                                                    const value = Number(e.target.value);
                                                    if (value >= 0 && value <= 100) {
                                                        this.setState({
                                                            estimate: {
                                                                ...estimate,
                                                                discount: Number(e.target.value),
                                                            },
                                                        });
                                                    }
                                                }}
                                                value={estimate.discount || ''}
                                                placeholder="additional discount"
                                                type="number"
                                            />
                                        </div>
                                        <div className="space-y-8 w-272">
                                            {lineItemDiscount > 0 && (
                                                <Fragment>
                                                    <div className="flex items-center justify-between">
                                                        <p className="uppercase text-11 font-700">
                                                            Total MRP:
                                                        </p>
                                                        <p className="text-13 text-darwin-neutrals-1000">
                                                            ₹{formattedNumber(totalMrp)}
                                                        </p>
                                                    </div>
                                                    <div className="flex items-center justify-between">
                                                        <p className="uppercase text-11 font-700">
                                                            Line item discount:
                                                        </p>
                                                        <p className="text-13 text-darwin-green-primary">
                                                            ₹{formattedNumber(lineItemDiscount)}
                                                        </p>
                                                    </div>
                                                </Fragment>
                                            )}
                                            <div className="flex items-center justify-between">
                                                <p className="uppercase text-11 font-700">
                                                    Total Amount:
                                                </p>
                                                <p className="text-13 text-darwin-neutrals-1000">
                                                    ₹{formattedNumber(totalAmount)}
                                                </p>
                                            </div>
                                            {additionalDiscount > 0 && (
                                                <div className="flex items-center justify-between">
                                                    <p className="uppercase text-11 font-700">
                                                        Additional Discount:
                                                    </p>
                                                    <p className="text-13 text-darwin-green-primary">
                                                        ₹{formattedNumber(additionalDiscount)}
                                                    </p>
                                                </div>
                                            )}
                                            <div className="h-1 bg-darwin-neutral_new-200"></div>
                                            <div className="flex items-center justify-between">
                                                <p className="uppercase text-11 font-700">
                                                    Amount to be paid:
                                                </p>
                                                <p className="text-13 text-darwin-neutrals-1000 font-700">
                                                    ₹{formattedNumber(amountToBePaid)}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="flex items-center justify-between px-16 py-12 bg-white border-t border-darwin-neutral_new-200">
                                <button
                                    className="px-9 py-5 rounded-4 border border-darwin-accent-bright-blue-800"
                                    onClick={closeMe}
                                >
                                    <p className="text-13 font-700 text-darwin-accent-bright-blue-800">
                                        Cancel
                                    </p>
                                </button>
                                <button
                                    className="px-10 py-6 flex items-center space-x-6 rounded-4 bg-darwin-accent-bright-blue-800 disabled:opacity-70"
                                    onClick={onFinish}
                                >
                                    <p className="text-13 font-700 text-white">Finish</p>
                                    {isSavingEstimate ? (
                                        <svg
                                            className="w-12 h-12 fill-white animate-spin"
                                            role="img"
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 512 512"
                                        >
                                            <path d="M96 256c0-26.5-21.5-48-48-48S0 229.5 0 256s21.5 48 48 48S96 282.5 96 256zM108.9 60.89c-26.5 0-48.01 21.49-48.01 47.99S82.39 156.9 108.9 156.9s47.99-21.51 47.99-48.01S135.4 60.89 108.9 60.89zM108.9 355.1c-26.5 0-48.01 21.51-48.01 48.01S82.39 451.1 108.9 451.1s47.99-21.49 47.99-47.99S135.4 355.1 108.9 355.1zM256 416c-26.5 0-48 21.5-48 48S229.5 512 256 512s48-21.5 48-48S282.5 416 256 416zM464 208C437.5 208 416 229.5 416 256s21.5 48 48 48S512 282.5 512 256S490.5 208 464 208zM403.1 355.1c-26.5 0-47.99 21.51-47.99 48.01S376.6 451.1 403.1 451.1s48.01-21.49 48.01-47.99S429.6 355.1 403.1 355.1zM256 0C229.5 0 208 21.5 208 48S229.5 96 256 96s48-21.5 48-48S282.5 0 256 0z"></path>
                                        </svg>
                                    ) : (
                                        <svg
                                            className="w-12 h-10 fill-white"
                                            viewBox="0 0 12 11"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path d="M11.4844 5.58203L7.01562 9.84766C6.76172 10.0762 6.38086 10.0762 6.15234 9.82227C5.92383 9.56836 5.92383 9.1875 6.17773 8.95898L9.55469 5.73438H0.921875C0.566406 5.73438 0.3125 5.48047 0.3125 5.125C0.3125 4.79492 0.566406 4.51562 0.921875 4.51562H9.55469L6.17773 1.31641C5.92383 1.08789 5.92383 0.681641 6.15234 0.453125C6.38086 0.199219 6.78711 0.199219 7.01562 0.427734L11.4844 4.69336C11.6113 4.82031 11.6875 4.97266 11.6875 5.125C11.6875 5.30273 11.6113 5.45508 11.4844 5.58203Z" />
                                        </svg>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </SlideFromRightWrapper>
            </Hotkeys>
        );
    }
}

export default CreateNewEstimates;
